import { FC, useCallback, useEffect, useMemo, useState } from 'react';

import { useRecoilValue } from 'recoil';

import {
  simpleBugoBrandListDataAtom,
  simpleBugoBrandListStatusAtom,
} from '@shared/api/bugoBrand/bugoBrand.atom';
import { useSimpleBugoBrandListHook } from '@shared/api/bugoBrand/bugoBrand.hook';
import { getDashboardGiftConversion } from '@shared/api/giftOrder/giftOrder.controller';
import {
  GiftConversionBucket,
  GiftConversionResponse,
} from '@shared/api/giftOrder/giftOrderDashboard.interface';

import {
  CenterLoading,
  ErrorBox,
  TD_CLASS,
  TH_CLASS,
} from '../../components/DashboardShared';
import { GiftConversionCalendar, HeatMetric } from './GiftConversionCalendar';
import { GiftConversionPacing } from './GiftConversionPacing';
import { GiftCourtesyFunnel } from './GiftCourtesyFunnel';
import {
  addMonths,
  daysInMonth,
  fmt,
  fmtWonCompact,
  getNiceScale,
  parseYm,
  toKstDayKey,
  ymOf,
} from './analysisUtils';

const isNotFound = (e: unknown) =>
  (e as { response?: { status?: number } })?.response?.status === 404;

type ViewMode = 'calendar' | 'courtesy' | 'summary' | 'trend' | 'pace' | 'table';
type RangePreset = '1m' | '3m' | '6m' | '12m';

const VIEW_LABELS: { key: ViewMode; label: string }[] = [
  { key: 'calendar', label: '달력' },
  { key: 'courtesy', label: '답례글 퍼널' },
  { key: 'summary', label: '요약' },
  { key: 'trend', label: '추이' },
  { key: 'pace', label: '지금 페이스' },
  { key: 'table', label: '표' },
];

const RANGE_LABELS: { key: RangePreset; label: string; months: number }[] = [
  { key: '1m', label: '1개월', months: 1 },
  { key: '3m', label: '3개월', months: 3 },
  { key: '6m', label: '6개월', months: 6 },
  { key: '12m', label: '12개월', months: 12 },
];

const pct = (num: number, den: number, digits = 1) =>
  den > 0 ? `${((num / den) * 100).toFixed(digits)}%` : '—';

const rate = (num: number, den: number) => (den > 0 ? num / den : 0);

/**
 * 발인일 범위 (KST 하루 경계 ISO). 달 경계에 맞춘다 — 달력이 달 단위로 그려져야
 * 하루하루가 제자리에 온다. endYm은 ◀▶로 과거로 미는 기준 달.
 */
const rangeOf = (months: number, endYm: string): { from: string; to: string } => {
  const { y, m } = parseYm(endYm);
  const startYm = addMonths(endYm, -(months - 1));
  const isCurrentMonth = endYm === ymOf(new Date());
  const lastDay = isCurrentMonth
    ? new Date().getDate() // 이번 달은 오늘까지
    : daysInMonth(y, m);
  return {
    from: new Date(`${startYm}-01T00:00:00+09:00`).toISOString(),
    to: new Date(
      `${endYm}-${String(lastDay).padStart(2, '0')}T23:59:59.999+09:00`,
    ).toISOString(),
  };
};

/**
 * 답례품 전환 분석 탭.
 *
 * 문제의식: "잘 팔려야 하는데 안 팔린다"를 매출로 보면 편차에 묻힌다 — 답례품은 한
 * 상가가 수백만 원을 몰아 사는 경우가 흔해 평균 매출이 대표값 구실을 못 한다.
 * 그래서 축을 상가(fevent) 수로 잡고 발인 상가 → 부고 공유 → 스토어 조회(이벤트
 * 로그) → 구매 단계별 전환율을 본다. 매출은 그 위에 얹어 본다.
 *
 * 스펙: docs/gift-conversion-analysis-spec.md
 */
export const GiftConversionTab: FC = function GiftConversionTab() {
  // 기본은 최근 1개월 — 하루하루를 보는 화면이라 좁게 시작하고, 기준을 넓히고 싶으면
  // 3/6/12개월로 늘린다
  const [preset, setPreset] = useState<RangePreset>('1m');
  // 추이·퍼널은 주별이 기본 — 1개월 구간에서 월별이면 막대가 하나뿐이라 급락이 안 보인다
  const [interval, setInterval] = useState<'month' | 'week'>('week');
  const [brandId, setBrandId] = useState('');
  const [teamType, setTeamType] = useState('');
  const [view, setView] = useState<ViewMode>('calendar');
  /** 기준 달 (◀▶로 과거 이동). 구간은 이 달로 끝난다 */
  const [endYm, setEndYm] = useState(() => ymOf(new Date()));
  const [heatMetric, setHeatMetric] = useState<HeatMetric>('achieved');

  useSimpleBugoBrandListHook();
  const brandListStatus = useRecoilValue(simpleBugoBrandListStatusAtom);
  const brandList = useRecoilValue(simpleBugoBrandListDataAtom);
  const selectedBrand = useMemo(
    () => brandList?.find((b) => b._id === brandId),
    [brandList, brandId],
  );
  const teamTypes = selectedBrand?.bugoAgency?.teamTypes ?? [];

  const [data, setData] = useState<GiftConversionResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [notReady, setNotReady] = useState(false);

  const months = RANGE_LABELS.find((r) => r.key === preset)?.months ?? 1;

  // 조회 구간은 뷰와 무관하게 프리셋 하나로 정한다. 달력만 일 단위,
  // 나머지는 월/주 단위 — 원시값으로 펼쳐 뷰 전환 때 불필요한 재조회를 막는다.
  const isCalendar = view === 'calendar';
  const { from: qFrom, to: qTo } = rangeOf(months, endYm);
  const qInterval = isCalendar ? 'day' : interval;

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    setNotReady(false);
    try {
      setData(
        await getDashboardGiftConversion(
          qFrom,
          qTo,
          brandId || undefined,
          teamType || undefined,
          qInterval,
        ),
      );
    } catch (e) {
      if (isNotFound(e)) setNotReady(true);
      else setError(e instanceof Error ? e.message : '전환 분석을 불러올 수 없습니다.');
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [qFrom, qTo, qInterval, brandId, teamType]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // 이벤트 로그 도입 이전 버킷은 조회 상가 0 — 전환율 급락으로 오독된다
  const logStartKey = data?.logCoverage.firstLogAt
    ? toKstDayKey(data.logCoverage.firstLogAt)
    : null;
  const isBeforeLog = useCallback(
    (b: GiftConversionBucket) => !!logStartKey && toKstDayKey(b.to) < logStartKey,
    [logStartKey],
  );

  const filterBar = (
    <div className="flex flex-wrap items-center gap-3">
      <div className="flex gap-1">
        {RANGE_LABELS.map((r) => (
          <button
            key={r.key}
            onClick={() => setPreset(r.key)}
            className={`rounded-lg px-3 py-1.5 text-xs font-medium ${
              preset === r.key
                ? 'bg-indigo-600 text-white'
                : 'bg-white text-gray-600 hover:bg-gray-50'
            }`}
          >
            {r.label}
          </button>
        ))}
      </div>

      {/* 기준 달 이동 — 구간은 이 달로 끝난다 */}
      <div className="flex items-center gap-1 rounded-lg bg-white p-1 shadow-sm">
        <button
          onClick={() => setEndYm(addMonths(endYm, -1))}
          className="rounded-md px-2.5 py-1 text-sm text-gray-500 hover:bg-gray-100"
          aria-label="이전 달"
        >
          ◀
        </button>
        <span className="min-w-[110px] text-center text-sm font-semibold text-gray-800">
          ~ {parseYm(endYm).y}년 {parseYm(endYm).m}월
        </span>
        <button
          onClick={() => setEndYm(addMonths(endYm, 1))}
          disabled={endYm >= ymOf(new Date())}
          className="rounded-md px-2.5 py-1 text-sm text-gray-500 hover:bg-gray-100 disabled:opacity-30"
          aria-label="다음 달"
        >
          ▶
        </button>
      </div>

      <select
        value={brandId}
        onChange={(e) => {
          setBrandId(e.target.value);
          setTeamType('');
        }}
        className="rounded-lg border border-gray-300 bg-white px-3 py-1.5 text-xs"
        disabled={brandListStatus !== 'success'}
      >
        <option value="">전체 상조</option>
        {brandListStatus === 'success' &&
          brandList?.map((brand) => (
            <option key={brand._id} value={brand._id}>
              {brand.bugoAgency?.corpName || brand._id}
            </option>
          ))}
      </select>

      {brandId && teamTypes.length > 0 && (
        <select
          value={teamType}
          onChange={(e) => setTeamType(e.target.value)}
          className="rounded-lg border border-gray-300 bg-white px-3 py-1.5 text-xs"
        >
          <option value="">전체 지사</option>
          {teamTypes.map((t) => (
            <option key={t} value={t}>
              {t}
            </option>
          ))}
        </select>
      )}

      {(view === 'trend' || view === 'table' || view === 'courtesy') && (
        <div className="flex gap-1">
          {(['month', 'week'] as const).map((iv) => (
            <button
              key={iv}
              onClick={() => setInterval(iv)}
              className={`rounded-full border px-2.5 py-0.5 text-xs font-medium ${
                interval === iv
                  ? 'border-indigo-600 bg-indigo-600 text-white'
                  : 'border-gray-300 bg-white text-gray-600 hover:bg-gray-50'
              }`}
            >
              {iv === 'month' ? '월별' : '주별'}
            </button>
          ))}
        </div>
      )}

      <div className="ml-auto flex gap-1">
        {VIEW_LABELS.map((v) => (
          <button
            key={v.key}
            onClick={() => setView(v.key)}
            className={`rounded-lg px-3 py-1.5 text-xs font-medium ${
              view === v.key
                ? 'bg-indigo-600 text-white'
                : 'bg-white text-gray-600 hover:bg-gray-50'
            }`}
          >
            {v.label}
          </button>
        ))}
      </div>
    </div>
  );

  if (loading && !data) return <CenterLoading />;
  if (notReady) {
    return (
      <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-6 text-center text-sm text-amber-800">
        답례품 전환 분석 API가 아직 이 서버에 배포되지 않았습니다. 배포 후 다시
        열어주세요.
      </div>
    );
  }
  if (error) return <ErrorBox message={error} onRetry={fetchData} />;
  if (!data) return null;

  const { totals, buckets, distribution } = data;

  return (
    <div className={`space-y-6 ${loading ? 'pointer-events-none opacity-60' : ''}`}>
      {filterBar}

      {/* KPI — 상가 축이 먼저, 매출은 그다음 */}
      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        <div className="rounded-lg bg-white p-4 shadow">
          <p className="text-xs text-gray-500">발인 상가</p>
          <p className="mt-1 text-2xl font-bold text-gray-900">
            {fmt(totals.feventCount)}곳
          </p>
          <p className="mt-1 text-xs text-gray-400">
            공유 {fmt(totals.sharedFeventCount)}곳 (
            {pct(totals.sharedFeventCount, totals.feventCount, 0)})
          </p>
        </div>
        <div className="rounded-lg bg-white p-4 shadow">
          <p className="text-xs text-gray-500">구매 상가 (전환율)</p>
          <p className="mt-1 text-2xl font-bold text-indigo-600">
            {pct(totals.purchasedFeventCount, totals.sharedFeventCount)}
          </p>
          <p className="mt-1 text-xs text-gray-400">
            {fmt(totals.purchasedFeventCount)}곳 / 공유 {fmt(totals.sharedFeventCount)}곳
            · 전체 기준 {pct(totals.purchasedFeventCount, totals.feventCount, 1)}
          </p>
        </div>
        <div className="rounded-lg bg-white p-4 shadow">
          <p className="text-xs text-gray-500">구매자 · 주문</p>
          <p className="mt-1 text-2xl font-bold text-gray-900">
            {fmt(totals.buyerCount)}명
          </p>
          <p className="mt-1 text-xs text-gray-400">
            주문 {fmt(totals.orderCount)}건 · 수량 {fmt(totals.quantity)}개 · 구매 상가당{' '}
            {rate(totals.orderCount, totals.purchasedFeventCount).toFixed(2)}건
          </p>
        </div>
        <div className="rounded-lg bg-white p-4 shadow">
          <p className="text-xs text-gray-500">답례품 매출</p>
          <p className="mt-1 text-2xl font-bold text-emerald-600">
            {fmt(totals.amount)}원
          </p>
          <p className="mt-1 text-xs text-gray-400">
            공유 상가당{' '}
            {fmtWonCompact(Math.round(rate(totals.amount, totals.sharedFeventCount)))}원 ·
            구매 상가 중앙값{' '}
            {distribution.amountPerPurchasedFevent
              ? fmtWonCompact(distribution.amountPerPurchasedFevent.median)
              : '—'}
            원
          </p>
        </div>
      </div>

      {view === 'courtesy' && (
        <GiftCourtesyFunnel
          data={data}
          logStartKey={logStartKey}
          isBeforeLog={isBeforeLog}
          brandId={brandId}
          teamType={teamType}
        />
      )}

      {view === 'calendar' && (
        <GiftConversionCalendar
          buckets={buckets}
          completeBufferDays={data.completeBufferDays}
          logStartKey={logStartKey}
          brandId={brandId}
          teamType={teamType}
          heatMetric={heatMetric}
          onHeatMetricChange={setHeatMetric}
        />
      )}

      {view === 'summary' && (
        <>
          <ExpectationBox data={data} />
          <FunnelBox totals={totals} logStartKey={logStartKey} />
          <DistributionBox data={data} />
        </>
      )}

      {view === 'trend' && (
        <TrendBox buckets={buckets} isBeforeLog={isBeforeLog} data={data} />
      )}

      {view === 'pace' && <GiftConversionPacing brandId={brandId} teamType={teamType} />}

      {view === 'table' && <BucketTable buckets={buckets} isBeforeLog={isBeforeLog} />}

      <p className="text-xs text-gray-400">
        발인일(KST) 기준 — 매출은 결제일이 아니라 그 상가의 발인일로 귀속된다(수익
        대시보드와 합계가 다른 게 정상). 완료 = 발인 +{data.completeBufferDays}일
        경과(답례품은 발인 +30일까지 주문 가능).
        {logStartKey && ` 조회 로그는 ${logStartKey}부터 쌓였다.`}
      </p>
    </div>
  );
};

// ─── 요약 뷰 ─────────────────────────────────────────

/** "몇 개 팔려야 정상인데 몇 개 팔렸나" — 완료 코호트만으로 기준 대비 평가 */
const ExpectationBox: FC<{ data: GiftConversionResponse }> = ({ data }) => {
  const { expectation } = data;
  const { baseline, target, expected, achievedRate } = expectation;

  if (!baseline || !expected || !achievedRate) {
    return (
      <div className="rounded-lg bg-white p-6 text-sm text-gray-500 shadow">
        <h3 className="mb-1 text-sm font-semibold text-gray-800">기대 대비 실적</h3>
        조회 구간 직전 1년의 완료 상가가 30곳 미만이라 기준선을 만들 수 없습니다 — 기간을
        앞당기거나 필터를 풀어주세요.
      </div>
    );
  }

  const tone = (r: number) =>
    r < 0.8 ? 'text-red-600' : r > 1.1 ? 'text-emerald-600' : 'text-gray-800';

  const rows: {
    label: string;
    expected: string;
    actual: string;
    ratio: number;
  }[] = [
    {
      label: '구매 상가',
      expected: `${expected.purchasedFeventCount}곳`,
      actual: `${fmt(target.purchasedFeventCount)}곳`,
      ratio: achievedRate.purchasedFevent,
    },
    {
      label: '주문 건수',
      expected: `${expected.orderCount}건`,
      actual: `${fmt(target.orderCount)}건`,
      ratio: achievedRate.order,
    },
    {
      label: '매출',
      expected: `${fmt(expected.amount)}원`,
      actual: `${fmt(target.amount)}원`,
      ratio: achievedRate.amount,
    },
  ];

  return (
    <div className="rounded-lg bg-white p-6 shadow">
      <h3 className="text-sm font-semibold text-gray-800">
        기대 대비 실적 — 판매가 끝난 상가만
      </h3>
      <p className="mt-0.5 mb-4 text-xs text-gray-400">
        기준: {toKstDayKey(baseline.from)} ~ {toKstDayKey(baseline.to)} 완료 상가{' '}
        {fmt(baseline.feventCount)}곳 (공유 {fmt(baseline.sharedFeventCount)}곳) — 공유
        상가당 구매 전환 {(baseline.purchaseRateShared * 100).toFixed(1)}% · 주문{' '}
        {baseline.ordersPerSharedFevent}건 · {fmt(baseline.amountPerSharedFevent)}원. 이
        기준을 조회 구간의 공유 상가 {fmt(target.sharedFeventCount)}곳에 곱한 값이
        기대치다.
      </p>
      <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
        {rows.map((r) => (
          <div key={r.label} className="rounded-lg bg-gray-50 px-4 py-3">
            <p className="text-xs text-gray-500">{r.label}</p>
            <p className={`mt-1 text-xl font-bold ${tone(r.ratio)}`}>
              {Math.round(r.ratio * 100)}%
            </p>
            <p className="mt-0.5 text-xs text-gray-500">
              기대 {r.expected} → 실제{' '}
              <span className="font-semibold text-gray-700">{r.actual}</span>
            </p>
          </div>
        ))}
      </div>
      <p className="mt-3 text-xs text-gray-400">
        아직 판매가 끝나지 않은 최근 발인분은 이 비교에서 빠진다 — 최근 실적은 "지금
        페이스" 뷰에서 진행도를 보정해 본다.
      </p>
    </div>
  );
};

/** 상가 단위 퍼널 — 발인 → 공유 → 조회 → 구매 */
const FunnelBox: FC<{
  totals: GiftConversionResponse['totals'];
  logStartKey: string | null;
}> = ({ totals, logStartKey }) => {
  const steps = [
    {
      label: '발인 상가',
      value: totals.feventCount,
      hint: '이벤트에 등록된 상가 전체',
      color: 'bg-gray-400',
    },
    {
      label: '부고 공유',
      value: totals.sharedFeventCount,
      hint: '공유가 없으면 답례품이 팔릴 경로가 없다',
      color: 'bg-blue-400',
    },
    {
      label: '답례인사 조회',
      value: totals.courtesyViewedFeventCount,
      hint: '답례인사 페이지 열람 로그',
      color: 'bg-indigo-400',
    },
    {
      label: '스토어 조회',
      value: totals.storeViewedFeventCount,
      hint: '스토어·상품 상세 열람 로그',
      color: 'bg-indigo-500',
    },
    {
      label: '구매',
      value: totals.purchasedFeventCount,
      hint: '1건 이상 결제',
      color: 'bg-emerald-500',
    },
  ];
  const max = Math.max(1, totals.feventCount);

  return (
    <div className="rounded-lg bg-white p-6 shadow">
      <h3 className="text-sm font-semibold text-gray-800">
        상가 단위 퍼널 (한 상가 = 1)
      </h3>
      <p className="mt-0.5 mb-4 text-xs text-gray-400">
        옆의 % 는 바로 위 단계 대비 전환율
        {logStartKey &&
          ` · 조회 단계는 이벤트 로그(${logStartKey}~)가 쌓인 구간에서만 유효하다`}
      </p>
      <div className="space-y-2.5">
        {steps.map((s, i) => {
          const prev = steps[i - 1];
          return (
            <div key={s.label}>
              <div className="mb-1 flex items-center justify-between text-xs">
                <span className="font-medium text-gray-700">{s.label}</span>
                <span className="text-gray-500">
                  <span className="font-bold text-gray-900">{fmt(s.value)}곳</span>
                  {prev && (
                    <span className="ml-2 text-gray-400">
                      직전 대비 {pct(s.value, prev.value)}
                    </span>
                  )}
                  <span className="ml-2 text-gray-400">
                    전체 {pct(s.value, totals.feventCount, 0)}
                  </span>
                </span>
              </div>
              <div className="h-6 w-full rounded bg-gray-100">
                <div
                  className={`h-full rounded ${s.color}`}
                  style={{ width: `${Math.max((s.value / max) * 100, 0.5)}%` }}
                />
              </div>
              <p className="mt-0.5 text-[11px] text-gray-400">{s.hint}</p>
            </div>
          );
        })}
      </div>
    </div>
  );
};

/** 구매 상가 안의 분포 — 왜 매출 평균을 믿으면 안 되는지 */
const DistributionBox: FC<{ data: GiftConversionResponse }> = ({ data }) => {
  const d = data.distribution;
  const amount = d.amountPerPurchasedFevent;
  const orders = d.ordersPerPurchasedFevent;
  const buyers = d.buyersPerPurchasedFevent;

  if (!amount) {
    return (
      <div className="rounded-lg bg-white p-6 text-sm text-gray-500 shadow">
        구매 상가가 없어 분포를 낼 수 없습니다.
      </div>
    );
  }

  return (
    <div className="rounded-lg bg-white p-6 shadow">
      <h3 className="text-sm font-semibold text-gray-800">
        구매 상가 {fmt(d.purchasedFeventCount)}곳의 분포
      </h3>
      <p className="mt-0.5 mb-4 text-xs text-gray-400">
        답례품은 한 상가가 몰아 사는 경우가 많아 평균 매출이 대표값 구실을 못 한다 —
        평균과 중앙값의 차이, 상위 상가의 매출 비중을 보고 판단한다
      </p>
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        <div className="rounded-lg bg-gray-50 px-4 py-3">
          <p className="text-xs text-gray-500">상가당 매출</p>
          <p className="mt-1 text-lg font-bold text-gray-900">
            중앙 {fmtWonCompact(amount.median)}원
          </p>
          <p className="mt-0.5 text-xs text-gray-400">
            평균 {fmtWonCompact(amount.mean)}원 · p90 {fmtWonCompact(amount.p90)}원 · 최대{' '}
            {fmtWonCompact(amount.max)}원
          </p>
        </div>
        <div className="rounded-lg bg-gray-50 px-4 py-3">
          <p className="text-xs text-gray-500">상위 상가 매출 비중</p>
          <p className="mt-1 text-lg font-bold text-gray-900">
            상위 5% → {(d.top5AmountShare * 100).toFixed(0)}%
          </p>
          <p className="mt-0.5 text-xs text-gray-400">
            상위 1% {(d.top1AmountShare * 100).toFixed(0)}% · 상위 10%{' '}
            {(d.top10AmountShare * 100).toFixed(0)}%
          </p>
        </div>
        <div className="rounded-lg bg-gray-50 px-4 py-3">
          <p className="text-xs text-gray-500">상가당 주문·구매자</p>
          <p className="mt-1 text-lg font-bold text-gray-900">
            {orders ? `${orders.median}건` : '—'} · {buyers ? `${buyers.median}명` : '—'}
          </p>
          <p className="mt-0.5 text-xs text-gray-400">
            중앙값 기준 · 최대 {orders?.max ?? '—'}건 / {buyers?.max ?? '—'}명
          </p>
        </div>
        <div className="rounded-lg bg-gray-50 px-4 py-3">
          <p className="text-xs text-gray-500">매출 변동계수</p>
          <p className="mt-1 text-lg font-bold text-gray-900">
            {d.amountCv === null ? '—' : d.amountCv.toFixed(2)}
          </p>
          <p className="mt-0.5 text-xs text-gray-400">
            1을 넘으면 편차가 평균만큼 커서 매출 지표는 흔들린다 — 전환율로 판단
          </p>
        </div>
      </div>
    </div>
  );
};

// ─── 추이 뷰 ─────────────────────────────────────────

const TrendBox: FC<{
  buckets: GiftConversionBucket[];
  isBeforeLog: (b: GiftConversionBucket) => boolean;
  data: GiftConversionResponse;
}> = ({ buckets, isBeforeLog, data }) => {
  const baseRate = data.expectation.baseline?.purchaseRateShared ?? null;
  const maxFevent = Math.max(1, ...buckets.map((b) => b.feventCount));
  const scale = getNiceScale(maxFevent, 3);
  const maxRate = Math.max(
    0.05,
    ...buckets.map((b) => rate(b.purchasedFeventCount, b.sharedFeventCount)),
    baseRate ?? 0,
  );

  return (
    <div className="space-y-6">
      <div className="rounded-lg bg-white p-6 shadow">
        <h3 className="text-sm font-semibold text-gray-800">상가 수와 구매 전환율</h3>
        <p className="mt-0.5 mb-4 text-xs text-gray-400">
          막대 = 발인 상가(옅은 부분은 미공유) · 선 = 공유 상가 대비 구매 전환율
          {baseRate !== null && ` · 점선 = 기준 전환율 ${(baseRate * 100).toFixed(1)}%`}
        </p>
        <div className="flex">
          <div className="relative mr-2 h-48 w-12 shrink-0">
            {scale.ticks.map((t) => (
              <span
                key={t}
                className="absolute right-0 translate-y-1/2 text-[10px] leading-none text-gray-400"
                style={{ bottom: `${(t / scale.niceMax) * 100}%` }}
              >
                {fmt(t)}
              </span>
            ))}
          </div>
          <div className="relative h-48 flex-1">
            {scale.ticks.map((t) => (
              <div
                key={t}
                className="pointer-events-none absolute inset-x-0 border-t border-gray-100"
                style={{ bottom: `${(t / scale.niceMax) * 100}%` }}
              />
            ))}
            {baseRate !== null && (
              <div
                className="pointer-events-none absolute inset-x-0 border-t border-dashed border-rose-300"
                style={{ bottom: `${(baseRate / maxRate) * 100}%` }}
              />
            )}
            <div className="absolute inset-0 flex items-end gap-1">
              {buckets.map((b) => {
                const r = rate(b.purchasedFeventCount, b.sharedFeventCount);
                return (
                  <div
                    key={b.key}
                    className="group relative flex h-full flex-1 flex-col items-center justify-end"
                  >
                    <div className="pointer-events-none absolute -top-1 left-1/2 z-10 hidden -translate-x-1/2 -translate-y-full whitespace-nowrap rounded bg-gray-800 px-2 py-1 text-[10px] leading-tight text-white shadow group-hover:block">
                      {b.key} · 상가 {fmt(b.feventCount)}곳(공유{' '}
                      {fmt(b.sharedFeventCount)}) · 구매 {fmt(b.purchasedFeventCount)}곳 (
                      {(r * 100).toFixed(1)}%) · 주문 {fmt(b.orderCount)}건 ·{' '}
                      {fmt(b.amount)}원{isBeforeLog(b) && ' · 조회 로그 없음'}
                    </div>
                    {/* 발인 상가 (옅음) */}
                    <div
                      className="w-full rounded-t bg-indigo-100"
                      style={{
                        height: `${(b.feventCount / scale.niceMax) * 100}%`,
                      }}
                    />
                    {/* 공유 상가 (진함, 겹쳐서) */}
                    <div
                      className="absolute bottom-0 w-full rounded-t bg-indigo-300"
                      style={{
                        height: `${(b.sharedFeventCount / scale.niceMax) * 100}%`,
                      }}
                    />
                    {/* 전환율 점 */}
                    <div
                      className="absolute left-1/2 h-2 w-2 -translate-x-1/2 translate-y-1/2 rounded-full bg-emerald-600"
                      style={{ bottom: `${(r / maxRate) * 100}%` }}
                    />
                  </div>
                );
              })}
            </div>
          </div>
        </div>
        <div className="mt-1 ml-14 flex gap-1">
          {buckets.map((b) => (
            <span
              key={b.key}
              className="flex-1 text-center text-[9px] leading-none text-gray-400"
            >
              {b.key.slice(2)}
            </span>
          ))}
        </div>
      </div>

      <div className="rounded-lg bg-white p-6 shadow">
        <h3 className="text-sm font-semibold text-gray-800">구매자 수와 매출</h3>
        <p className="mt-0.5 mb-4 text-xs text-gray-400">
          매출은 편차가 커서 방향만 본다 — 판단은 위의 전환율로
        </p>
        <MiniBars
          buckets={buckets}
          value={(b) => b.buyerCount}
          label="구매자"
          hex="#6366f1"
          fmtValue={(v) => `${fmt(v)}명`}
        />
        <MiniBars
          buckets={buckets}
          value={(b) => b.amount}
          label="매출"
          hex="#10b981"
          fmtValue={(v) => `${fmt(v)}원`}
          fmtTick={fmtWonCompact}
          showXAxis
        />
      </div>
    </div>
  );
};

const MiniBars: FC<{
  buckets: GiftConversionBucket[];
  value: (b: GiftConversionBucket) => number;
  label: string;
  hex: string;
  fmtValue: (v: number) => string;
  fmtTick?: (v: number) => string;
  showXAxis?: boolean;
}> = ({ buckets, value, label, hex, fmtValue, fmtTick = fmt, showXAxis }) => {
  const scale = getNiceScale(Math.max(...buckets.map(value), 1), 3);
  return (
    <div className="mb-3">
      <p className="mb-1 flex items-center gap-1.5 text-xs font-medium text-gray-600">
        <span
          className="inline-block h-2.5 w-2.5 rounded-sm"
          style={{ backgroundColor: hex }}
        />
        {label}
      </p>
      <div className="flex">
        <div className="relative mr-2 h-24 w-12 shrink-0">
          {scale.ticks.map((t) => (
            <span
              key={t}
              className="absolute right-0 translate-y-1/2 text-[10px] leading-none text-gray-400"
              style={{ bottom: `${(t / scale.niceMax) * 100}%` }}
            >
              {fmtTick(t)}
            </span>
          ))}
        </div>
        <div className="relative h-24 flex-1">
          {scale.ticks.map((t) => (
            <div
              key={t}
              className="pointer-events-none absolute inset-x-0 border-t border-gray-100"
              style={{ bottom: `${(t / scale.niceMax) * 100}%` }}
            />
          ))}
          <div className="absolute inset-0 flex items-end gap-1">
            {buckets.map((b) => {
              const v = value(b);
              return (
                <div
                  key={b.key}
                  className="group relative flex h-full flex-1 flex-col items-center justify-end"
                >
                  <div className="pointer-events-none absolute -top-1 left-1/2 z-10 hidden -translate-x-1/2 -translate-y-full whitespace-nowrap rounded bg-gray-800 px-2 py-1 text-[10px] text-white shadow group-hover:block">
                    {b.key} · {label} {fmtValue(v)}
                  </div>
                  <div
                    className="w-full rounded-t"
                    style={{
                      height: `${(v / scale.niceMax) * 100}%`,
                      minHeight: v > 0 ? 2 : 0,
                      backgroundColor: hex,
                    }}
                  />
                </div>
              );
            })}
          </div>
        </div>
      </div>
      {showXAxis && (
        <div className="mt-1 ml-14 flex gap-1">
          {buckets.map((b) => (
            <span
              key={b.key}
              className="flex-1 text-center text-[9px] leading-none text-gray-400"
            >
              {b.key.slice(2)}
            </span>
          ))}
        </div>
      )}
    </div>
  );
};

// ─── 표 뷰 ───────────────────────────────────────────

const BucketTable: FC<{
  buckets: GiftConversionBucket[];
  isBeforeLog: (b: GiftConversionBucket) => boolean;
}> = ({ buckets, isBeforeLog }) => (
  <div className="overflow-hidden rounded-lg bg-white shadow">
    <div className="border-b px-4 py-3">
      <h3 className="text-sm font-semibold text-gray-800">구간별 상세</h3>
    </div>
    <div className="max-h-[560px] overflow-x-auto overflow-y-auto">
      <table className="min-w-full divide-y divide-gray-200">
        <thead className="sticky top-0 z-10 bg-gray-50">
          <tr>
            <th className={TH_CLASS}>구간</th>
            <th className={`${TH_CLASS} text-right`}>발인 상가</th>
            <th className={`${TH_CLASS} text-right`}>공유</th>
            <th className={`${TH_CLASS} text-right`}>스토어 조회</th>
            <th className={`${TH_CLASS} text-right`}>구매 상가</th>
            <th className={`${TH_CLASS} text-right`}>전환율</th>
            <th className={`${TH_CLASS} text-right`}>구매자</th>
            <th className={`${TH_CLASS} text-right`}>주문</th>
            <th className={`${TH_CLASS} text-right`}>수량</th>
            <th className={`${TH_CLASS} text-right`}>매출</th>
            <th className={`${TH_CLASS} text-right`}>완료 상가</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100">
          {buckets.map((b) => (
            <tr key={b.key} className="hover:bg-gray-50">
              <td className={`${TD_CLASS} font-medium`}>
                {b.key}
                {b.completeFeventCount < b.feventCount && (
                  <span className="ml-1 text-[10px] text-amber-600">판매 진행 중</span>
                )}
              </td>
              <td className={`${TD_CLASS} text-right`}>{fmt(b.feventCount)}</td>
              <td className={`${TD_CLASS} text-right`}>{fmt(b.sharedFeventCount)}</td>
              <td className={`${TD_CLASS} text-right`}>
                {isBeforeLog(b) ? (
                  <span className="text-gray-300">로그 없음</span>
                ) : (
                  fmt(b.storeViewedFeventCount)
                )}
              </td>
              <td className={`${TD_CLASS} text-right`}>{fmt(b.purchasedFeventCount)}</td>
              <td className={`${TD_CLASS} text-right font-semibold`}>
                {pct(b.purchasedFeventCount, b.sharedFeventCount)}
              </td>
              <td className={`${TD_CLASS} text-right`}>{fmt(b.buyerCount)}</td>
              <td className={`${TD_CLASS} text-right`}>{fmt(b.orderCount)}</td>
              <td className={`${TD_CLASS} text-right`}>{fmt(b.quantity)}</td>
              <td className={`${TD_CLASS} text-right`}>{fmt(b.amount)}원</td>
              <td className={`${TD_CLASS} text-right text-gray-400`}>
                {fmt(b.completeFeventCount)}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  </div>
);
